import { CheckCircleOutlined, CloseCircleOutlined, LoadingOutlined, SyncOutlined } from '@ant-design/icons';
import { PageController } from '@client/components/_core/layout/PageController';
import { StatusBadge } from '@client/components/_ui/display/StatusBadge';
import { Loading } from '@client/components/_ui/feedback/Loading';
import { Space } from '@client/components/_ui/layout/Space';
import { tokenValues } from '@client/design-system/__generated__';
import { useFormatter } from '@client/hooks/utils/useFormatter';
import { trpcOptions } from '@client/trpc/client';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Alert, Card, Col, Divider, Row, Tag, Typography } from 'antd';

const { Title, Text } = Typography;

export const Route = createFileRoute('/status')({
    component: StatusPage,
});

function StatusPage() {
    const { formatDateTime } = useFormatter();

    // Get local build version
    const localBuildNumber = __APP_BUILD_NUMBER__?.toString() || '0';

    // Fetch server version
    const {
        data: serverVersion,
        isLoading,
        error,
    } = useQuery(trpcOptions.appSetting.getAppBuildVersion.queryOptions());

    // Extract server build number from version string (format: "major.minor.patch.buildNumber")
    const serverBuildNumber = serverVersion?.buildNumber || serverVersion?.version.split('.').pop() || '0';

    // Check if versions match
    const versionsMatch = localBuildNumber === serverBuildNumber;

    return (
        <PageController route={Route}>
            <div className="fixed inset-0 z-10 grid place-items-center bg-muted">
                <Card className="w-full max-w-2xl shadow-lg">
                    <Title level={2} className="mb-6 text-center">
                        <SyncOutlined className="mr-2" />
                        System Status
                    </Title>

                    <Row gutter={[16, 24]}>
                        <Col span={12}>
                            <Card
                                type="inner"
                                title="Client Version"
                                className="h-full"
                                styles={{ header: { backgroundColor: tokenValues.color.gray[100] } }}
                            >
                                <Space orientation="vertical" className="w-full">
                                    <div>
                                        <Text type="secondary">Build Number:</Text>
                                        <div className="mt-1">
                                            <Tag variant="outlined" color="cyan" className="text-base">
                                                <span id="client-build-number">{localBuildNumber}</span>
                                            </Tag>
                                        </div>
                                    </div>
                                </Space>
                            </Card>
                        </Col>

                        <Col span={12}>
                            <Card
                                type="inner"
                                title="Server Version"
                                className="h-full"
                                styles={{ header: { backgroundColor: tokenValues.color.gray[100] } }}
                            >
                                {isLoading ? (
                                    <div className="flex h-full items-center justify-center py-8">
                                        <Loading indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
                                    </div>
                                ) : error ? (
                                    <Alert
                                        title="Connection Error"
                                        description="Unable to fetch server version"
                                        type="error"
                                        showIcon
                                    />
                                ) : (
                                    <Space orientation="vertical" className="w-full">
                                        <div>
                                            <Text type="secondary">Version:</Text>
                                            <div className="mt-1">
                                                <Tag variant="outlined" color="purple" className="text-base">
                                                    {serverVersion?.version || 'Unknown'}
                                                </Tag>
                                            </div>
                                        </div>
                                        <div>
                                            <Text type="secondary">Build Number:</Text>
                                            <div className="mt-1">
                                                <Tag variant="outlined" color="magenta" className="text-base">
                                                    <span id="server-build-number">{serverBuildNumber}</span>
                                                </Tag>
                                            </div>
                                        </div>
                                        {serverVersion?.updatedAt && (
                                            <div>
                                                <Text type="secondary">Last Updated:</Text>
                                                <div className="mt-1">
                                                    <Text>
                                                        {formatDateTime(
                                                            new Date(serverVersion.updatedAt),
                                                            'dd/MM/yyyy HH:mm:ss',
                                                        )}
                                                    </Text>
                                                </div>
                                            </div>
                                        )}
                                    </Space>
                                )}
                            </Card>
                        </Col>
                    </Row>

                    <Divider />

                    <div className="mt-4">
                        {isLoading ? (
                            <Alert title="Checking system status..." type="info" showIcon icon={<LoadingOutlined />} />
                        ) : error ? (
                            <Alert
                                title="System Check Failed"
                                description="Unable to verify system status. Please check your connection."
                                type="error"
                                showIcon
                                icon={<CloseCircleOutlined />}
                            />
                        ) : versionsMatch ? (
                            <Alert
                                title="System Healthy"
                                description="Client and server versions are synchronized. All systems operational."
                                type="success"
                                showIcon
                                icon={<CheckCircleOutlined />}
                            />
                        ) : (
                            <Alert
                                title="Version Mismatch"
                                description={`Client build (${localBuildNumber}) and server build (${serverBuildNumber}) are different. This may cause compatibility issues. Please refresh the page or contact support if the issue persists.`}
                                type="warning"
                                showIcon
                            />
                        )}
                    </div>

                    <div className="mt-6 text-center">
                        <Space size="large">
                            <Text type="secondary">
                                Environment: <Tag variant="outlined">{import.meta.env.VITE_ENV || 'development'}</Tag>
                            </Text>
                            <Text type="secondary">
                                Status Check: <StatusBadge module="state" status="HEALTHY" customLabel="Operational" />
                            </Text>
                        </Space>
                    </div>
                </Card>
            </div>
        </PageController>
    );
}
